import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import apiKeysService from '../../services/apiKeysService';
import FacilityAssignmentTree from '../common/FacilityAssignmentTree';

const APIKeyForm = ({ open, apiKey, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    organizationId: '',
    scopes: [],
    isActive: true,
    environment: 'development',
    ipWhitelist: [],
    rateLimit: {
      requestsPerMinute: 60,
      requestsPerHour: 1000
    },
    expiresAt: null,
    assignedFacilities: []
  });

  const [errors, setErrors] = useState({});
  const [newIp, setNewIp] = useState('');

  useEffect(() => {
    if (apiKey) {
      setFormData(prev => ({
        ...prev,
        ...apiKey,
        rateLimit: {
          ...prev.rateLimit,
          ...apiKey.rateLimit
        },
        assignedFacilities: apiKey.assignedFacilities || []
      }));
    } else {
      // Reset form for new API key
      setFormData({
        name: '',
        description: '',
        organizationId: '',
        scopes: [],
        isActive: true,
        environment: 'development',
        ipWhitelist: [],
        rateLimit: {
          requestsPerMinute: 60,
          requestsPerHour: 1000
        },
        expiresAt: null,
        assignedFacilities: []
      });
    }
    setErrors({});
  }, [apiKey, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const handleAddIp = () => {
    if (newIp.trim() && !formData.ipWhitelist.includes(newIp.trim())) {
      handleChange('ipWhitelist', [...formData.ipWhitelist, newIp.trim()]);
      setNewIp('');
    }
  };

  const handleRemoveIp = (ipToRemove) => {
    handleChange('ipWhitelist', formData.ipWhitelist.filter(ip => ip !== ipToRemove));
  };

  const handleScopeToggle = (scope) => {
    const newScopes = formData.scopes.includes(scope)
      ? formData.scopes.filter(s => s !== scope)
      : [...formData.scopes, scope];
    handleChange('scopes', newScopes);
  };

  const validateForm = () => {
    const validation = apiKeysService.validateApiKey(formData);
    setErrors(validation.errors.reduce((acc, error) => {
      const field = error.toLowerCase().includes('name') ? 'name' :
                   error.toLowerCase().includes('description') ? 'description' :
                   error.toLowerCase().includes('organization') ? 'organizationId' :
                   error.toLowerCase().includes('scope') ? 'scopes' :
                   error.toLowerCase().includes('ip') ? 'ipWhitelist' :
                   'general';
      acc[field] = error;
      return acc;
    }, {}));
    
    return validation.valid;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        {apiKey ? 'Edit API Key' : 'Create API Key'}
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 3 }}>
        {errors.general && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.general}
          </Alert>
        )}

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Basic Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name *"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  placeholder="e.g., Billing System Integration"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description *"
                  multiline
                  rows={2}
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  error={!!errors.description}
                  helperText={errors.description}
                  placeholder="Describe the purpose and usage of this API key"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Organization ID *"
                  value={formData.organizationId}
                  onChange={(e) => handleChange('organizationId', e.target.value)}
                  error={!!errors.organizationId}
                  helperText={errors.organizationId}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Environment</InputLabel>
                  <Select
                    value={formData.environment}
                    label="Environment"
                    onChange={(e) => handleChange('environment', e.target.value)}
                  >
                    <MenuItem value="development">Development</MenuItem>
                    <MenuItem value="staging">Staging</MenuItem>
                    <MenuItem value="production">Production</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => handleChange('isActive', e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Access Scopes</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Select the scopes this API key will have access to:
                </Typography>
                {errors.scopes && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.scopes}
                  </Alert>
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {apiKeysService.getAvailableScopes().map((scope) => (
                    <Chip
                      key={scope}
                      label={scope}
                      onClick={() => handleScopeToggle(scope)}
                      color={formData.scopes.includes(scope) ? 'primary' : 'default'}
                      variant={formData.scopes.includes(scope) ? 'filled' : 'outlined'}
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Rate Limiting</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Requests per Minute"
                  type="number"
                  value={formData.rateLimit.requestsPerMinute}
                  onChange={(e) => handleNestedChange('rateLimit', 'requestsPerMinute', parseInt(e.target.value) || 60)}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Requests per Hour"
                  type="number"
                  value={formData.rateLimit.requestsPerHour}
                  onChange={(e) => handleNestedChange('rateLimit', 'requestsPerHour', parseInt(e.target.value) || 1000)}
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon />
              <Typography variant="h6">Facilities</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select which facilities this API key can access. Only requests for data from these facilities will be allowed.
            </Typography>
            <FacilityAssignmentTree
              selectedFacilities={formData.assignedFacilities}
              onChange={(facilityIds) => handleChange('assignedFacilities', facilityIds)}
              showSummary={true}
              maxHeight={300}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Security Settings</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <DateTimePicker
                  label="Expiration Date"
                  value={formData.expiresAt}
                  onChange={(newValue) => handleChange('expiresAt', newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: 'Leave empty for no expiration'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  IP Whitelist
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Only requests from these IP addresses will be allowed. Leave empty to allow all IPs.
                </Typography>
                {errors.ipWhitelist && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.ipWhitelist}
                  </Alert>
                )}
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={10}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Add IP Address"
                        value={newIp}
                        onChange={(e) => setNewIp(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddIp()}
                        placeholder="e.g., 192.168.1.1"
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleAddIp}
                        disabled={!newIp.trim()}
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
                <List>
                  {formData.ipWhitelist.map((ip) => (
                    <ListItem key={ip}>
                      <ListItemText primary={ip} />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleRemoveIp(ip)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          {apiKey ? 'Update' : 'Create'} API Key
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default APIKeyForm;
